import React, { useState, useEffect, useMemo } from "react";
import {
  administrationCompanies,
  adminRoleName,
  supportLabel,
} from "services/constants";
import {
  checkEditorPermission,
  checkViewerPermission,
  checkFirstLevelApproverPermission,
  checkSecondLevelApproverPermission,
  getAdministrationRedirectPath,
  isProvisioningLoaded,
} from "services/utils/HelperFunction";
import { Loader } from "components/Loader";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useSetupCreate } from "components/SetupFlow/progress";
import { useSelector, useDispatch } from "react-redux";
import { showToast } from "services/utils/Status";
import * as Mui from "@mui/material";
import { ThemeProvider } from "@emotion/react";
import { customGetAPI } from "services/utils/CustomAPI";
import { AddCompany } from "./AddCompany";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { setCurrentScreen } from "services/Redux/userManagament";
import sessionHandling from "services/utils/notificationUtils";
import { themes } from "services/constants";
import {
  setCompanyList,
  setCompanyDetails,
  setTopbarCompanyName,
} from "services/Redux/userToken";
import { FilterProvider } from "pages/FilterContext";
import { designTheme } from "services/constants/designTokens";
import { useExpandablePanel } from "services/hooks/useExpandablePanel";
import { companiesHeaderRow, companyUi } from "../adminListScreenUi";
import { CompanyStatCards } from "./CompanyStatCards";
import { CompanyDetailPanel } from "./CompanyDetailPanel";
import { ListView } from "./ListView";

export const Companies = () => {
  const {
    domain,
    token,
    globalSearchValue,
    userScreenAccess,
    userDetails,
    topbarCompanyId,
    // Aliased -- this screen already has a local `companyDetails` (the
    // filtered admin table rows, below); this is the separate redux-cached
    // full company list the Sidebar's logo/name reads from.
    companyDetails: cachedCompanyDetails,
  } = useSelector((state) => state.tokenReducer);
  const [masterDataForSearch, setMasterDataForSearch] = useState([]);
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  // Starts true (not false) — ListView gates its table on this and the
  // fetch below only fires from an effect, several renders after mount.
  // Defaulting to false let that window render the empty-state message
  // before the fetch had even started, flashing empty -> loader -> real
  // rows on every fresh visit to Companies (same fix as Roster's `loading`).
  const [loader, setLoader] = useState(true);
  const [getCompanyDetails, setGetCompanyDetails] = useState(true);
  // The Companies screen manages the tenant's own list of companies -- the
  // backend `company/` endpoint already returns every company the caller is
  // entitled to, unfiltered by any company_id. topbarCompanyId is the
  // "viewing as company X" switcher used to scope *other* screens' data
  // (Users, Leave, etc.) to one company at a time; filtering this list by it
  // as well collapsed the table down to just the single selected company
  // (e.g. only "Strco") even though the API response held all of them. See
  // Administration/index.js's own topbarCompanyId usage for the pattern this
  // screen should NOT mirror.
  const companyDetails = useMemo(
    () => globalSearchValue
      ? masterDataForSearch.filter((item) =>
          administrationCompanies.some((key) =>
            item[key]?.toString().toLowerCase()
              .includes(globalSearchValue.toString().toLowerCase())
          )
        )
      : masterDataForSearch,
    [globalSearchValue, masterDataForSearch]
  );
  const [addCompanyScreen, setAddCompanyScreen] = useState(false);
  const [companyApi, setCompanyApi] = useState(false);
  const [screenName, setScreenName] = useState("Create Company");
  const [editCompanyDetails, setEditCompanyDetails] = useState(null);
  const [editDetails, setEditDetails] = useState(null);
  const [updateData, setUpdateData] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogActionType, setDialogActionType] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogCompanyDetails, setDialogCompanyDetails] = useState(null);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [panelCompany, setPanelCompany] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelEditMode, setPanelEditMode] = useState(false);
  const [statusCardFilter, setStatusCardFilter] = useState(null);
  const {
    expanded: createCompanyExpanded,
    toggleExpanded: toggleCreateCompanyExpanded,
  } = useExpandablePanel(addCompanyScreen);
  const {
    expanded: companyPanelExpanded,
    toggleExpanded: toggleCompanyPanelExpanded,
  } = useExpandablePanel(panelOpen);

  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Companies",
    "Company"
  );

  // Companies has no real approval workflow, but the permission flag still
  // exists on the submodule -- per the user's split, the status-changing
  // actions (Primary, Inactivate/Activate, Delete Company, and the panel's
  // standalone Deactivate/Activate button) require this instead of plain
  // editor access, while the Edit action stays
  // editor-gated. An edit-only user gets the edit-side actions, an
  // approve-only user gets the status-change actions, both grants show
  // everything.
  const hasApproverAccess =
    checkFirstLevelApproverPermission(
      userScreenAccess?.module_details,
      "Companies",
      "Company"
    ) ||
    checkSecondLevelApproverPermission(
      userScreenAccess?.module_details,
      "Companies",
      "Company"
    );

  // The sidebar hiding this screen's link when viewer access is revoked
  // (ADMINISTRATION_VIEWER_GATED_MODULES in Sidebar/index.js) only hides the
  // menu row -- routes.js has no per-route access guard anywhere in the app
  // (Admin.js maps every route straight to its component), so a refresh
  // while already on this screen, a bookmark, or the back/forward button
  // still rendered the full company list and data with no check at all.
  // This closes that specific gap for Companies; the same fix is applied to
  // Users, Roles, User Group, and Notification Settings.
  const hasViewAccess = checkViewerPermission(
    userScreenAccess?.module_details,
    "Companies",
    "Company"
  );

  const history = useHistory();
  // Redux's userScreenAccess starts out as last session's cached value (or
  // the empty array default) until this page's own provisioning fetch
  // resolves -- see isProvisioningLoaded. Without this gate, a hard refresh
  // redirected away immediately off that stale/empty snapshot, before the
  // real fetch had a chance to answer, which looked like the provisioning
  // API was never even called.
  const provisioningLoaded = isProvisioningLoaded(userScreenAccess, "Administration");

  // A static "Access Restricted" message was a dead end -- send the user
  // somewhere they can actually see instead (another Administration screen
  // first, then the same "first accessible product" fallback the login flow
  // already uses). Runs as an effect, not during render, since navigating
  // is a side effect.
  useEffect(() => {
    if (!provisioningLoaded || hasViewAccess) return;
    history.replace(
      getAdministrationRedirectPath(userScreenAccess?.module_details),
    );
  }, [provisioningLoaded, hasViewAccess, userScreenAccess, history]);

  const companiesModule = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Companies"
  );
  const subModuleId = companiesModule?.submodules?.find(
    (sub) => sub.sub_module_name === "Company"
  )?.sub_module_id;

  useEffect(() => {
    const getCompany = async () => {
      setLoader(true);
      const res = await customGetAPI(`${domain}company/`);
      if (res.ok) {
        const results = Array.isArray(res.responseDetails)
          ? res.responseDetails
          : res.responseDetails?.results ?? [];
        // Derived, stable string fields for the shared statusChip/CustomFilter
        // machinery — CustomFilter's autocomplete filter needs labels, not
        // raw booleans, same as Users' own `status` field.
        const withDerivedFields = results.map((item) => ({
          ...item,
          status: item.isactive ? "Active" : "Inactive",
          companyType: item.isprimary ? "Primary" : "Secondary",
        }));
        if (withDerivedFields.length === 0) addNewCompany();
        setMasterDataForSearch(withDerivedFields);
        setLoader(false);
      } else {
        setLoader(false);
      }
    };
    if (getCompanyDetails) {
      getCompany();
      setGetCompanyDetails(false);
    }
  }, [domain, getCompanyDetails, token]);

  const activatedAndDeactivateCompany = (params, companyDetails) => {
    let message = "";
    if (params === "Activate") {
      message = (
        <span>
          Are you sure you want to activate{" "}
          <b style={{ color: themes.redColor }}>{companyDetails.companyname}</b>
          ?
        </span>
      );
    } else if (params === "Inactivate") {
      message = (
        <sapn>
          Are you sure you want to inactivate{" "}
          <b style={{ color: themes.redColor }}>{companyDetails.companyname}</b>
          ?
        </sapn>
      );
    }

    setDialogMessage(message);
    setDialogCompanyDetails(companyDetails);
    setDialogActionType(params);
    setOpenDialog(true);
  };

  const handleDialogAction = async (confirm) => {
    if (confirm) {
      // Perform the action based on dialogActionType
      if (
        dialogActionType === "Activate" ||
        dialogActionType === "Inactivate"
      ) {
        await activatedAndDeactivateCompanyConfirmed(
          dialogActionType,
          dialogCompanyDetails
        );
      }
    }
  };

  useEffect(() => {
    const fetchDepartmentData = async () => {
      try {
        const response = await fetch(
          `${domain}user-group/user/company/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          }
        );
        const res = await response.json();
        if (response.ok) {
          dispatch(setCompanyList(res));
          localStorage.setItem("companyAccess", JSON.stringify(res));
          dispatch(setCurrentScreen("User"));
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error('REQUEST_FAILED');
        }
      } catch (error) {
        console.error('[Index] operation failed:', error);
        showToast("error", "Couldn't load company details. Please refresh the page.");
      }
    };
    if (
      companyApi &&
      [adminRoleName, supportLabel].includes(userDetails?.type)
    ) {
      fetchDepartmentData();
      setCompanyApi(false);
    }
  }, [companyApi, dispatch, domain, token, userDetails, userDetails?.id]);

  const addNewCompany = () => {
    setEditCompanyDetails({
      companyname: "",
      companycode: "",
      address: "",
      gstno: "",
      email: "",
      phonenumber: "",
      isactive: true,
      primary: false,
    });
    setScreenName("Create Company");
    setUpdateData(true);
    setAddCompanyScreen(true);
  };

  const activatedAndDeactivateCompanyConfirmed = async (
    actionType,
    companyDetails
  ) => {
    setButtonLoader(true);
    try {
      const myHeaders = new Headers();
      const formData = new FormData();
      myHeaders.append("Authorization", `token ${token}`);
      formData.append("isactive", actionType === "Activate" ? true : false);
      const response = await fetch(`${domain}company/${companyDetails.id}/`, {
        method: "PUT",
        headers: myHeaders,
        body: formData,
        redirect: "follow",
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error);
      } else {
        setGetCompanyDetails(true);
        setCompanyApi(true);
        if (actionType === "Activate") {
          showToast("success", "Company activated.");
        } else if (actionType === "Inactivate") {
          showToast("success", "Company deactivated.");
        } else {
          showToast("success", "Company updated.");
        }
      }
    } catch (error) {
      console.error('[Index] operation failed:', error);
      showToast("error", "Couldn't save the company details. Please try again.");
    } finally {
      setOpenDialog(false);
      setButtonLoader(false);
    }
  };
  useEffect(() => {
    setPage(0);
  }, [globalSearchValue]);

  const changeCompanyType = async () => {
    try {
      const myHeaders = new Headers();
      const formData = new FormData();
      myHeaders.append("Authorization", `token ${token}`);
      formData.append("isprimary", editDetails.isprimary ? false : true);
      const response = await fetch(`${domain}company/${editDetails?.id}/`, {
        method: "PUT",
        headers: myHeaders,
        body: formData,
        redirect: "follow",
      });
      // const res = await response.json();
      if (response.ok) {
        const companyType = editDetails.isprimary ? "Secondary" : "Primary";
        showToast(
          "success",
          `${editDetails.companyname} is now set as ${companyType} company.You can switch back anytime.`
        );
        setGetCompanyDetails(true);
        setCompanyApi(true);
      } else if (response.status === 409) {
        sessionHandling();
      } else {
        throw new Error('REQUEST_FAILED');
      }
    } catch (error) {
      console.error('[Index] operation failed:', error);
      showToast("error", "Couldn't save the company details. Please try again.");
    }
  };

  const handleStatCardClick = (key) => {
    setPage(0);
    if (key === "all") {
      setStatusCardFilter(null);
    } else {
      setStatusCardFilter((prev) => (prev === key ? null : key));
    }
  };

  const handleEditFromPanel = (companyToEdit) => {
    if (!check_access) {
      showToast(
        "error",
        "Access denied: You are not authorized to edit company"
      );
      return;
    }
    setPanelCompany(companyToEdit);
    setPanelEditMode(true);
    setPanelOpen(true);
  };

  const handleSavePanelEdit = async (
    companyToEdit,
    data,
    logoFile,
    removeCompanyLogo = false
  ) => {
    if (!check_access) {
      showToast(
        "error",
        "Access denied: You are not authorized to edit company"
      );
      return false;
    }

    try {
      const myHeaders = new Headers();
      const formData = new FormData();
      myHeaders.append("Authorization", `token ${token}`);
      if (logoFile) {
        formData.append("company_logo", logoFile, logoFile.name);
      }
      if (removeCompanyLogo) {
        formData.append("remove_company_logo", "true");
      }
      formData.append("companyname", data.companyname);
      formData.append("company_code", data.companycode);
      formData.append("address", data.address);
      formData.append("gstnumber", data.gstno);
      formData.append("email", data.email);
      formData.append("isactive", true);
      formData.append("phonenumber", data.phoneno);
      formData.append("isprimary", companyToEdit.isprimary);

      const response = await fetch(`${domain}company/${companyToEdit.id}/`, {
        method: "PUT",
        headers: myHeaders,
        body: formData,
        redirect: "follow",
      });
      const responseData = await response.json();

      if (!response.ok) {
        if (response.status === 409) {
          sessionHandling();
          return false;
        }
        showToast(
          "error",
          "Couldn't save the company details. Please try again."
        );
        return false;
      }

      const companyAccessResponse = await fetch(
        `${domain}user-group/user/company/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
        }
      );
      const companyAccessResult = await companyAccessResponse.json();
      if (companyAccessResponse.ok) {
        dispatch(setCompanyList(companyAccessResult));
        localStorage.setItem(
          "companyAccess",
          JSON.stringify(companyAccessResult)
        );
      }

      if (topbarCompanyId === companyToEdit.id) {
        dispatch(setTopbarCompanyName(data.companyname));
      }

      const updatedCompany = {
        ...companyToEdit,
        companyname: data.companyname,
        company_code: data.companycode,
        gstnumber: data.gstno,
        email: data.email,
        phonenumber: data.phoneno,
        address: data.address,
        company_logo_url: removeCompanyLogo
          ? null
          : logoFile
            ? responseData?.company_logo_url || URL.createObjectURL(logoFile)
            : companyToEdit.company_logo_url,
      };

      setMasterDataForSearch((prev) =>
        prev.map((item) =>
          item.id === companyToEdit.id ? updatedCompany : item
        )
      );
      // Keep the cache the Sidebar's logo/company-name reads from in sync --
      // it's populated once at login and otherwise only refreshed on a hard
      // reload, so without this a saved logo/name change wouldn't show up
      // there until the user logged out and back in.
      const patchedCompanyDetails = (cachedCompanyDetails ?? []).map((item) =>
        item.id === companyToEdit.id ? { ...item, ...updatedCompany } : item
      );
      dispatch(setCompanyDetails(patchedCompanyDetails));
      localStorage.setItem(
        "companyDetails",
        JSON.stringify({ results: patchedCompanyDetails })
      );
      setPanelCompany(updatedCompany);
      setPanelEditMode(false);
      setGetCompanyDetails(true);
      setCompanyApi(true);
      showToast("success", "Company updated successfully");
      return true;
    } catch (error) {
      console.error("[Companies] operation failed:", error);
      showToast("error", "Couldn't save the company details. Please try again.");
      return false;
    }
  };

  const handleDeleteInactiveCompany = async (companyToDelete) => {
    // Grouped with Primary/Inactivate/Activate under approve access, per
    // the user's split -- deleting a company is at least as consequential
    // as inactivating one.
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to delete company"
      );
      return false;
    }

    try {
      const myHeaders = new Headers();
      const formData = new FormData();
      myHeaders.append("Authorization", `token ${token}`);
      formData.append("isdeleted", true);
      const response = await fetch(
        `${domain}company/${companyToDelete.id}/`,
        {
          method: "PUT",
          headers: myHeaders,
          body: formData,
          redirect: "follow",
        }
      );
      await response.json();

      if (!response.ok) {
        if (response.status === 409) {
          sessionHandling();
          return false;
        }
        showToast("error", "Couldn't delete the company. Please try again.");
        return false;
      }

      setGetCompanyDetails(true);
      setPanelOpen(false);
      setPanelEditMode(false);
      showToast("success", "Company deleted.");
      return true;
    } catch (error) {
      console.error("[Companies] operation failed:", error);
      showToast("error", "Couldn't delete the company. Please try again.");
      return false;
    }
  };

  // Setup flow: OK on its dialog lands here with the create form open.
  useSetupCreate(
    "company",
    () => addNewCompany(),
    provisioningLoaded && hasViewAccess && !loader && Boolean(check_access),
  );

  if (!provisioningLoaded) {
    return <Loader info="Loading..." />;
  }

  if (!hasViewAccess) {
    return <Loader info="Redirecting..." />;
  }

  return (
    <ThemeProvider theme={designTheme}>
      <Mui.Box sx={companyUi.page}>
        <Mui.Box sx={{ px: 2, pt: { xs: 7, sm: 2 } }}>
          {companiesHeaderRow({
            title: "Companies",
            subtitle:
              "Manage the companies registered under this tenant and their details.",
            primaryLabel: "Add Company",
            // Hide the button rather than show it and block the click --
            // same treatment TeamZone Approve's NewRequestButton already
            // gives its own create action for a non-editor.
            showPrimaryAction: check_access,
            onPrimaryClick: () => {
              if (check_access) {
                addNewCompany();
              } else {
                showToast(
                  "error",
                  "Access denied: You are not authorized to add company"
                );
              }
            },
          })}
        </Mui.Box>
        <CompanyStatCards
          companies={companyDetails}
          onCardClick={handleStatCardClick}
          activeFilter={statusCardFilter}
        />
        <FilterProvider
          domain={domain}
          userDetails={userDetails}
          subModuleId={subModuleId}
        >
          <ListView
            isLoading={loader}
            masterDataForSearch={masterDataForSearch}
            allCompanies={companyDetails}
            page={page}
            setPage={setPage}
            onRowClick={(data) => {
              setPanelCompany(data);
              setPanelEditMode(false);
              setPanelOpen(true);
            }}
            selectedCompanyId={panelOpen ? panelCompany?.id : null}
            statusFilter={statusCardFilter}
            subModuleId={subModuleId}
            // Download, sort, filter, and saved views require viewer access.
            viewAccess={hasViewAccess}
            hasApproverAccess={hasApproverAccess}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            editDetails={editDetails}
            changeCompanyType={changeCompanyType}
            activatedAndDeactivateCompany={activatedAndDeactivateCompany}
          />
        </FilterProvider>
      </Mui.Box>
      {editCompanyDetails && (
        <AddCompany
          open={addCompanyScreen}
          expanded={createCompanyExpanded}
          screenName={screenName}
          editEompanyDetails={editCompanyDetails}
          setAddCompanyScreen={setAddCompanyScreen}
          updateData={updateData}
          setUpdateData={setUpdateData}
          companyDetails={companyDetails}
          setGetCompanyDetails={setGetCompanyDetails}
          setCompanyApi={setCompanyApi}
          onClose={() => setAddCompanyScreen(false)}
          onCreated={() => setGetCompanyDetails(true)}
          onToggleExpand={toggleCreateCompanyExpanded}
        />
      )}
      <CompanyDetailPanel
        open={panelOpen}
        onClose={() => {
          setPanelOpen(false);
          setPanelEditMode(false);
        }}
        expanded={companyPanelExpanded}
        onToggleExpand={toggleCompanyPanelExpanded}
        company={panelCompany}
        checkAccess={check_access}
        hasApproverAccess={hasApproverAccess}
        startInEditMode={panelEditMode}
        onMoreActions={(company, event) => {
          setEditDetails(company);
          setAnchorEl(event?.currentTarget || null);
        }}
        onEdit={handleEditFromPanel}
        onSaveEdit={handleSavePanelEdit}
        onPermanentDelete={handleDeleteInactiveCompany}
        onDeactivate={(company) => {
          setPanelOpen(false);
          // Same status-change action as the More Actions menu's Inactivate/
          // Activate item -- approve-gated to match, not editor-gated.
          if (hasApproverAccess) {
            activatedAndDeactivateCompany(
              company?.isactive ? "Inactivate" : "Activate",
              company
            );
          } else {
            showToast(
              "error",
              "Access denied: You are not authorized to change company status"
            );
          }
        }}
      />
      <GlobalConfirmDialog
        open={openDialog}
        title="Confirmation"
        onCancel={() => setOpenDialog(false)}
        onConfirm={() => handleDialogAction(true)}
        loading={openDialog && buttonLoader}
        danger={dialogActionType === "Inactivate"}
      >
        {dialogMessage}
      </GlobalConfirmDialog>
    </ThemeProvider>
  );
};
